import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Star, MapPin, ShoppingCart, Check, Store, ArrowRight, MessageSquare, User, Heart, Share2, X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, Eye, Zap, Truck, Sparkles, Flame } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import StockBadge, { LOW_STOCK_THRESHOLD } from '../components/StockBadge';
import ProductImageDownloader from '../components/ProductImageDownloader';
import { resolveImageUrl, handleImgError, IMAGE_PLACEHOLDER } from '../utils/imageUtils';
import Seo, { SITE_URL } from '../components/Seo';
import { useInstantDeliveryLabel } from '../hooks/useInstantDeliveryLabel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ProductDetail = () => {
  const instantDeliveryLabel = useInstantDeliveryLabel();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewMode = location.pathname.startsWith('/vendor/preview');
  const { addToCart, updateQuantity, cartItems } = useCart();
  const { user, getAuthHeaders } = useAuth();
  const [product, setProduct] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [vendor, setVendor] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [moreFromVendor, setMoreFromVendor] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [wishlists, setWishlists] = useState([]);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = React.useRef({ x: 0, y: 0 });
  const panStart = React.useRef({ x: 0, y: 0 });

  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setZoom(z => {
      const next = z + (e.deltaY < 0 ? 0.3 : -0.3);
      const clamped = Math.max(1, Math.min(5, next));
      if (clamped === 1) setPan({ x: 0, y: 0 });
      return clamped;
    });
  };

  const handlePointerDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...pan };
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    setPan({
      x: panStart.current.x + (e.clientX - dragStart.current.x),
      y: panStart.current.y + (e.clientY - dragStart.current.y),
    });
  };

  const handlePointerUp = () => setDragging(false);

  const handleDoubleClick = () => {
    if (zoom > 1) resetZoom();
    else { setZoom(2.5); setPan({ x: 0, y: 0 }); }
  };

  // Reset zoom when changing images
  React.useEffect(() => { resetZoom(); }, [selectedImage]);

  useEffect(() => {
    fetchProductDetails();
    window.scrollTo(0, 0);
  }, [id]);

  // Track recently viewed products in localStorage
  useEffect(() => {
    if (!id) return;
    const key = 'mybuzzar_recently_viewed';
    const viewed = JSON.parse(localStorage.getItem(key) || '[]');
    const updated = [id, ...viewed.filter(pid => pid !== id)].slice(0, 15);
    localStorage.setItem(key, JSON.stringify(updated));
  }, [id]);

  // Auto-select size when only 1 is available for the selected color
  useEffect(() => {
    if (!product || !selectedColor) return;
    const inv = product.inventory || {};
    const ci = inv[selectedColor];
    if (!ci) return;
    const availSizes = Object.entries(ci)
      .filter(([k, v]) => k !== '_qty' && parseInt(v) > 0)
      .map(([s]) => s);
    if (availSizes.length === 1 && !selectedSize) {
      setSelectedSize(availSizes[0]);
    }
  }, [selectedColor, product, selectedSize]);

  // Fetch wishlists to check if product is in any
  useEffect(() => {
    if (!user || !id) return;
    axios.get(`${API_URL}/api/wishlists`, { headers: getAuthHeaders() })
      .then(res => {
        const wl = res.data || [];
        setWishlists(wl);
        setIsInWishlist(wl.some(w => w.items?.includes(id)));
      }).catch(() => {});
  }, [user, id]);

  const toggleWishlist = async () => {
    if (!user) { toast.error('Please login to add to wishlist'); return; }
    setWishlistLoading(true);
    try {
      let targetWl = wishlists[0];
      if (!targetWl) {
        const res = await axios.post(`${API_URL}/api/wishlists`, { name: 'My Wishlist' }, { headers: getAuthHeaders() });
        targetWl = res.data;
        setWishlists([targetWl]);
      }
      await axios.post(`${API_URL}/api/wishlists/${targetWl.id}/toggle-product/${id}`, {}, { headers: getAuthHeaders() });
      setIsInWishlist(!isInWishlist);
      toast.success(isInWishlist ? 'Removed from wishlist' : 'Added to wishlist');
    } catch (e) { toast.error('Failed to update wishlist'); }
    setWishlistLoading(false);
  };

  const shareProduct = async () => {
    if (!product) return;
    const shareUrl = window.location.href;
    const title = product.name || 'MyBuzzar product';
    const priceLine = product.price ? ` — ₹${product.price}${product.mrp_price && product.mrp_price > product.price ? ` (was ₹${product.mrp_price})` : ''}` : '';
    const text = `Check out "${title}"${priceLine} on MyBuzzar — ${vendor?.shop_name ? `from ${vendor.shop_name}` : 'a local seller near you'}.`;
    // Native share (mobile + supported desktop browsers) — opens the OS share
    // sheet. Falls back to clipboard if cancelled or unsupported.
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (err) {
        // User-cancelled — silent. Other failures (incl. clipboard fallback) below.
        if (err && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${shareUrl}`);
      toast.success('Link copied — paste it anywhere to share');
    } catch {
      toast.error('Could not share — please copy the page URL manually');
    }
  };

  const fetchProductDetails = async () => {
    try {
      const productResponse = await axios.get(`${API_URL}/api/products/${id}`, user ? { headers: getAuthHeaders() } : {});
      const productData = productResponse.data;
      setProduct(productData);

      const vendorResponse = await axios.get(`${API_URL}/api/vendors/${productData.vendor_id}`);
      setVendor(vendorResponse.data);
      setVendorProfile(vendorResponse.data);

      // Get more products from this vendor
      try {
        const vendorProductsRes = await axios.get(`${API_URL}/api/vendors/${productData.vendor_id}/products`);
        const otherProducts = (vendorProductsRes.data.products || [])
          .filter(p => p.id !== id && p.stock_quantity > 0)
          .slice(0, 4);
        setMoreFromVendor(otherProducts);
      } catch (e) {
        console.debug('Could not fetch vendor products');
      }

      const reviewsResponse = await axios.get(`${API_URL}/api/products/${id}/reviews`);
      setReviews(reviewsResponse.data.reviews || reviewsResponse.data || []);

      // Fetch colour-merge siblings (other products that the vendor/admin has
      // grouped together as the same item in different colours)
      try {
        const sibRes = await axios.get(`${API_URL}/api/products/${id}/siblings`);
        setSiblings(sibRes.data?.siblings || []);
      } catch { setSiblings([]); }

      // Check if user can review this product (completed purchase required)
      if (user) {
        try {
          const canReviewRes = await axios.get(`${API_URL}/api/products/${id}/can-review`, { headers: getAuthHeaders() });
          setCanReview(canReviewRes.data.can_review);
        } catch { setCanReview(false); }
      }

      // Do NOT auto-select size — customer must choose
      if (productData.colors && productData.colors.length > 0) {
        setSelectedColor(productData.colors[0]);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (isPreviewMode) {
      toast.info('Preview mode — Add to Cart is disabled for vendors viewing their own product');
      return false;
    }
    if (isOutOfStock) {
      toast.error('This product is out of stock');
      return false;
    }
    if (!canAddToCart) {
      if (availableColors.length > 0 && !selectedColor) toast.error('Please select a color');
      else if (showSizes && !selectedSize) toast.error('Please select a size');
      else toast.error('Selected combination is out of stock');
      return false;
    }
    if (!product) return false;
    
    // Only require color/size selection when there are AVAILABLE options to choose from
    const inv = product.inventory || {};
    const hasInv = Object.keys(inv).length > 0;
    const invColors = hasInv
      ? Object.entries(inv).filter(([_, s]) => typeof s === 'object' && Object.values(s).some(q => parseInt(q) > 0)).map(([c]) => c)
      : [];
    
    if (invColors.length > 0 && !selectedColor) {
      toast.error('Please select a color');
      return false;
    }
    if (invColors.length > 0 && selectedColor) {
      const szAvail = inv[selectedColor] ? Object.entries(inv[selectedColor]).filter(([k, v]) => k !== '_qty' && parseInt(v) > 0) : [];
      if (szAvail.length > 0 && !selectedSize) {
        const NO_SZ = ['Jewelry', 'Electronics', 'Home Decor', 'Organic Food', 'Accessories'];
        const needsSize = !NO_SZ.some(c => (product?.category || '').toLowerCase().includes(c.toLowerCase()));
        if (needsSize) {
          toast.error('Please select a size');
          return false;
        }
      }
    }

    // Fallback: if no inventory matrix but product has sizes defined and size picker is visible, enforce selection
    if (!hasInv && showSizes && sizesForColor.length > 0 && !selectedSize) {
      toast.error('Please select a size');
      return false;
    }

    const cartProduct = { ...product, price: effectivePrice, mrp_price: effectiveMrp };
    addToCart(cartProduct, quantity, selectedSize, selectedColor);
    toast.success('Added to cart!');
    return true;
  };

  // Buy Now — add (if not already in cart) then take the customer to the CART
  // (not checkout). The cart shows the product + details and, if the customer
  // isn't logged in yet, prompts login there before proceeding to place the
  // order. Jumping straight to /checkout used to bounce guests to /browse.
  const handleBuyNow = () => {
    if (isPreviewMode) {
      toast.info('Preview mode — Buy Now is disabled');
      return;
    }
    if (inCartQty > 0) { navigate('/cart'); return; }
    if (handleAddToCart()) navigate('/cart');
  };

  // Cart quantity stepper handlers (bound to the selected color/size combo).
  const incrementCartQty = () => {
    const max = selectedStock !== null && selectedStock > 0 ? selectedStock : 99;
    if (inCartQty >= max) { toast.error('No more stock available'); return; }
    updateQuantity(product.id, selectedSize, selectedColor, inCartQty + 1);
  };
  const decrementCartQty = () => {
    updateQuantity(product.id, selectedSize, selectedColor, inCartQty - 1);
  };

  const isOutOfStock = (() => {
    if (!product) return true;
    // If stock_quantity > 0, product is in stock regardless of inventory details
    if ((product.stock_quantity || 0) > 0) return false;
    const inv = product.inventory || {};
    const hasInv = Object.keys(inv).length > 0;
    if (hasInv) {
      const totalStock = Object.values(inv).reduce((sum, sizes) => 
        sum + (typeof sizes === 'object' ? Object.values(sizes).reduce((s, q) => s + (parseInt(q) || 0), 0) : 0), 0);
      return totalStock === 0;
    }
    return true;
  })();

  const canAddToCart = (() => {
    if (!product || isOutOfStock) return false;
    const inv = product.inventory || {};
    const hasInv = Object.keys(inv).length > 0;
    if (!hasInv) return true;
    // Get colors that actually have stock in inventory
    const invColors = Object.entries(inv).filter(([_, s]) => typeof s === 'object' && Object.values(s).some(q => parseInt(q) > 0)).map(([c]) => c);
    // If no inventory colors have stock but stock_quantity > 0, allow add to cart without selection
    if (invColors.length === 0) return true;
    // Colors available - require selection
    if (!selectedColor) return false;
    const NO_SZ = ['Jewelry', 'Electronics', 'Home Decor', 'Organic Food', 'Accessories'];
    const szForColor = (inv[selectedColor])
      ? Object.entries(inv[selectedColor]).filter(([k, v]) => k !== '_qty' && parseInt(v) > 0).map(([s]) => s) : [];
    const needsSize = !NO_SZ.some(c => (product?.category || '').toLowerCase().includes(c.toLowerCase())) && szForColor.length > 0;
    if (needsSize && !selectedSize) return false;
    if (selectedColor) {
      const ci = inv[selectedColor];
      if (!ci) return false;
      let stk = 0;
      if (ci['_qty']) stk = parseInt(ci['_qty']) || 0;
      else if (selectedSize) stk = parseInt(ci[selectedSize]) || 0;
      if (stk <= 0 && szForColor.length > 0) return false;
    }
    return true;
  })();

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }

    setSubmittingReview(true);
    try {
      await axios.post(
        `${API_URL}/api/products/${id}/reviews`,
        {
          rating: reviewRating,
          comment: reviewText,
          images: []
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Review submitted successfully!');
      setReviewText('');
      setReviewRating(5);
      fetchProductDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20" data-testid="loading-indicator">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="text-center py-20">
          <p className="text-xl text-muted-foreground">Product not found</p>
        </div>
      </div>
    );
  }

  const variantPrices = product?.variant_prices || {};
  const variantMrp = product?.variant_mrp || {};
  const variantStock = product?.variant_stock || {};
  const hasVariantPricing = Object.keys(variantPrices).length > 0;
  const hasFlatVariants = (product?.sizes || []).length > 0 &&
    (product?.sizes || []).some(s => variantPrices[s] != null || variantStock[s] != null);

  // The cheapest flat size-variant (used as the default display before the
  // customer picks a size — shows min price with THAT variant's own MRP).
  const cheapestVariantSize = (() => {
    if (!hasFlatVariants) return null;
    let minS = null; let minP = Infinity;
    (product?.sizes || []).forEach(s => {
      const p = Number(variantPrices[s]);
      if (!isNaN(p) && p > 0 && p < minP) { minP = p; minS = s; }
    });
    return minS;
  })();

  // Get effective price for selected color/size
  const getVariantPrice = () => {
    // Flat size-keyed variants (new model): variant_prices[size] = price
    if (selectedSize && variantPrices[selectedSize] != null && typeof variantPrices[selectedSize] !== 'object') {
      const n = Number(variantPrices[selectedSize]);
      if (!isNaN(n) && n > 0) return n;
    }
    // No size chosen yet → default to the cheapest variant's price.
    if (!selectedSize && cheapestVariantSize != null) {
      const n = Number(variantPrices[cheapestVariantSize]);
      if (!isNaN(n) && n > 0) return n;
    }
    if (!hasVariantPricing) return product.price;
    if (selectedColor && variantPrices[selectedColor]) {
      const colorPrices = variantPrices[selectedColor];
      if (selectedSize && colorPrices[selectedSize]) return colorPrices[selectedSize];
      if (colorPrices['_default']) return colorPrices['_default'];
      const firstVal = Object.values(colorPrices)[0];
      if (typeof firstVal === 'number') return firstVal;
    }
    return product.price;
  };
  const getVariantMrp = () => {
    if (selectedSize && variantMrp[selectedSize] != null && typeof variantMrp[selectedSize] !== 'object') {
      const n = Number(variantMrp[selectedSize]);
      if (!isNaN(n) && n > 0) return n;
    }
    // No size chosen yet → MRP of the cheapest variant.
    if (!selectedSize && cheapestVariantSize != null) {
      const n = Number(variantMrp[cheapestVariantSize]);
      if (!isNaN(n) && n > 0) return n;
    }
    if (!hasVariantPricing) return product.mrp_price || 0;
    if (selectedColor && variantMrp[selectedColor]) {
      const colorMrp = variantMrp[selectedColor];
      if (selectedSize && colorMrp[selectedSize]) return colorMrp[selectedSize];
      if (colorMrp['_default']) return colorMrp['_default'];
      const firstVal = Object.values(colorMrp)[0];
      if (typeof firstVal === 'number') return firstVal;
    }
    return product.mrp_price || 0;
  };

  const effectivePrice = getVariantPrice();
  const effectiveMrp = getVariantMrp();
  const displayPrice = effectivePrice;
  const mrpPrice = effectiveMrp ? effectiveMrp : null;
  const hasMrpDiscount = mrpPrice && effectiveMrp > effectivePrice;
  const discountPct = hasMrpDiscount
    ? Math.round(((effectiveMrp - effectivePrice) / effectiveMrp) * 100)
    : null;
  const inventory = product?.inventory || {};
  const hasInventory = Object.keys(inventory).length > 0;
  
  // Get images to display based on selected color
  const colorImages = product?.color_images || {};
  const displayImages = ((selectedColor && colorImages[selectedColor]?.length > 0)
    ? colorImages[selectedColor]
    : (product?.images?.length > 0 ? product.images : [])
  ).filter(img => img && typeof img === 'string' && img.trim() !== '');
  
  // Determine which colors have stock — and dedupe case-insensitively so
  // legacy products that stored both "Red" and "red" don't render two buttons.
  const rawColors = hasInventory
    ? Object.entries(inventory).filter(([_, sizes]) => {
        if (typeof sizes === 'object') {
          return Object.values(sizes).some(q => parseInt(q) > 0);
        }
        return false;
      }).map(([c]) => c)
    : (product?.colors || []);
  const seenColorKeys = new Set();
  const availableColors = [];
  rawColors.forEach((c) => {
    const k = (c || '').trim().toLowerCase();
    if (!k || seenColorKeys.has(k)) return;
    seenColorKeys.add(k);
    availableColors.push(c);
  });

  // Determine which sizes are available for the selected color
  const sizesForColor = (selectedColor && hasInventory && inventory[selectedColor])
    ? Object.entries(inventory[selectedColor])
        .filter(([k, v]) => k !== '_qty' && parseInt(v) > 0)
        .map(([s]) => s)
    : (product?.sizes || []);

  const NO_SIZE_CATEGORIES = ['Jewelry', 'Electronics', 'Home Decor', 'Organic Food'];
  const showSizes = !NO_SIZE_CATEGORIES.some(c => (product?.category || '').toLowerCase().includes(c.toLowerCase())) && sizesForColor.length > 0;

  // Get stock for selected combo
  const getSelectedStock = () => {
    // Flat size-keyed variants (new model)
    if (hasFlatVariants && !hasInventory) {
      if (!selectedSize) return null;
      return parseInt(variantStock[selectedSize]) || 0;
    }
    if (!hasInventory) return product.stock_quantity;
    if (!selectedColor) return null;
    const colorInv = inventory[selectedColor];
    if (!colorInv) return 0;
    if (colorInv['_qty']) return parseInt(colorInv['_qty']) || 0;
    if (!selectedSize) return null;
    return parseInt(colorInv[selectedSize]) || 0;
  };
  const selectedStock = getSelectedStock();

  // How many of THIS exact combo (product + colour + size) are already in cart.
  const cartLine = cartItems.find(i =>
    i.id === product?.id &&
    (i.selectedSize || '') === (selectedSize || '') &&
    (i.selectedColor || '') === (selectedColor || '')
  );
  const inCartQty = cartLine?.quantity || 0;

  // Total units available across the whole product (matrix or flat) — used for
  // the "last piece left" scarcity tag on the product preview.
  const productTotalStock = (() => {
    const inv = product?.inventory || {};
    if (Object.keys(inv).length > 0) {
      return Object.values(inv).reduce((sum, sizes) => sum + (typeof sizes === 'object'
        ? Object.values(sizes).reduce((s, q) => s + (parseInt(q) || 0), 0) : 0), 0);
    }
    return product?.stock_quantity || 0;
  })();
  const isLastItem = !isOutOfStock && productTotalStock === 1;
  const isNewProduct = (() => {
    if (!product?.created_at) return false;
    const t = new Date(product.created_at).getTime();
    if (Number.isNaN(t)) return false;
    return Date.now() - t <= 7 * 24 * 60 * 60 * 1000;
  })();

  return (
    <div className="min-h-screen bg-background">
      {!isPreviewMode && product && (
        <Seo
          title={product.name}
          description={(product.description || `${product.name} available now on MyBuzzar`).slice(0, 160)}
          path={`/product/${product.id}`}
          type="product"
          image={displayImages.length > 0 ? resolveImageUrl(displayImages[0]) : undefined}
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.name,
            description: (product.description || product.name || '').slice(0, 500),
            image: displayImages.map((img) => resolveImageUrl(img)).filter(Boolean),
            ...(product.brand && product.brand !== 'Generic' ? { brand: { '@type': 'Brand', name: product.brand } } : {}),
            ...(product.category ? { category: product.category } : {}),
            offers: {
              '@type': 'Offer',
              priceCurrency: 'INR',
              price: String(displayPrice || product.price || 0),
              availability: isOutOfStock ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
              url: `${SITE_URL}/product/${product.id}`,
            },
          }}
        />
      )}
      <Navbar />

      {isPreviewMode && (
        <div className="bg-purple-600 text-white px-4 py-2.5 flex items-center justify-between flex-wrap gap-2 shadow-md" data-testid="vendor-preview-banner">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Eye className="h-4 w-4" />
            Preview mode — this is exactly how customers see your product
          </div>
          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={() => navigate('/vendor/enhanced-dashboard')} data-testid="exit-preview-btn">
            ← Back to Dashboard
          </Button>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-8 pb-28 lg:pb-8" data-testid="product-detail-page">
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 mb-8 sm:mb-12">
          {/* Product Images */}
          <div className="space-y-4 min-w-0">
            <div
              className="aspect-square bg-white rounded-lg overflow-hidden border border-stone-200 cursor-pointer touch-pan-y select-none"
              data-testid="product-main-image"
              onClick={() => setLightboxOpen(true)}
              onTouchStart={(e) => { window.__pdtSX = e.touches[0].clientX; window.__pdtSY = e.touches[0].clientY; }}
              onTouchEnd={(e) => {
                const sx = window.__pdtSX || 0;
                const sy = window.__pdtSY || 0;
                const ex = e.changedTouches[0].clientX;
                const ey = e.changedTouches[0].clientY;
                const dx = ex - sx;
                const dy = ey - sy;
                if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
                  e.stopPropagation();
                  if (dx < 0 && selectedImage < displayImages.length - 1) setSelectedImage(i => i + 1);
                  else if (dx > 0 && selectedImage > 0) setSelectedImage(i => i - 1);
                  window.__pdtSX = null; // consume swipe — suppress the click/lightbox
                }
              }}
              onClickCapture={(e) => {
                if (window.__pdtSX === null) { e.stopPropagation(); e.preventDefault(); window.__pdtSX = 0; }
              }}
            >
              {displayImages.length > 0 ? (
                <img
                  src={resolveImageUrl(displayImages[selectedImage] || displayImages[0])}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  loading="eager"
                  onError={handleImgError}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-400">
                  No Image Available
                </div>
              )}
            </div>
            {displayImages.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-3">
                {displayImages.slice(0, 5).map((img, idx) => (
                  <div
                    key={idx}
                    className={`aspect-square bg-white rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                      selectedImage === idx ? 'border-primary ring-2 ring-primary/20' : 'border-stone-200 hover:border-primary/40'
                    }`}
                    onClick={() => setSelectedImage(idx)}
                    data-testid={`product-thumb-${idx}`}
                  >
                    <img src={resolveImageUrl(img)} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" onError={handleImgError} />
                  </div>
                ))}
              </div>
            )}
            {/* Admin + owning-vendor download panel — lets them grab JPEGs
                of any/all product images, optionally with an Instagram-style
                price sticker stamped onto the bottom-right. Visible in:
                  • Customer product page when user is admin (admins can hit
                    /admin/product/:id which bypasses the customer redirect)
                  • Vendor preview mode when the vendor owns the product
                Always hidden for regular customers. */}
            <ProductImageDownloader
              images={displayImages}
              productName={product?.name}
              price={effectivePrice || product?.price}
              allowed={
                user?.user_type === 'admin' ||
                (user?.user_type === 'vendor' && product?.vendor_id === user?.id)
              }
            />
          </div>

          {/* Product Details */}
          <div className="space-y-6 min-w-0">
            <div>
              {product.brand && product.brand !== 'Generic' && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="branded-tag">#Branded</span>
                  <span className="text-sm font-medium text-stone-600" data-testid="product-brand">{product.brand}</span>
                </div>
              )}
              {product.brand === 'Generic' && (
                <p className="text-xs text-muted-foreground mb-1">Generic Product</p>
              )}
              <div className="flex items-start justify-between gap-3">
                <h1 className="text-3xl sm:text-4xl font-playfair font-bold break-words min-w-0 flex-1" data-testid="product-title">{product.name}</h1>
                <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                  <button
                    onClick={shareProduct}
                    className="p-2.5 rounded-full border border-stone-200 hover:bg-purple-50 hover:border-purple-200 transition-all"
                    data-testid="share-product-btn"
                    title="Share this product"
                    aria-label="Share product"
                  >
                    <Share2 className="h-5 w-5 text-stone-500 hover:text-purple-600 transition-colors" />
                  </button>
                  <button
                    onClick={toggleWishlist}
                    disabled={wishlistLoading}
                    className="p-2.5 rounded-full border border-stone-200 hover:bg-red-50 hover:border-red-200 transition-all"
                    data-testid="wishlist-heart-btn"
                  >
                    <Heart className={`h-5 w-5 transition-colors ${isInWishlist ? 'fill-red-500 text-red-500' : 'text-stone-400'}`} />
                  </button>
                </div>
              </div>
            </div>

            {product.rating > 0 && (
              <div className="flex items-center space-x-2" data-testid="product-rating">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={`rating-star-${i}`}
                      className={`h-5 w-5 ${
                        i < Math.floor(product.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-stone-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="font-medium">{product.rating}</span>
                <span className="text-muted-foreground">({product.review_count} reviews)</span>
              </div>
            )}

            {/* Newly-added & scarcity tags — surfaced right at the top of the
                product preview so shoppers instantly see freshness/urgency. */}
            {(isNewProduct || isLastItem) && (
              <div className="flex flex-wrap items-center gap-2" data-testid="product-preview-tags">
                {isNewProduct && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-3 py-1 shadow-sm shadow-emerald-200" data-testid="detail-newly-added-tag">
                    <Sparkles className="h-3.5 w-3.5 fill-white/40" /> Newly Added
                  </span>
                )}
                {isLastItem && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 shadow-sm shadow-orange-200 animate-pulse" data-testid="detail-last-item-tag">
                    <Flame className="h-3.5 w-3.5 fill-white/40" /> Last item left!
                  </span>
                )}
              </div>
            )}

            {/* Get It Right Now — eye-catching ribbon above price */}
            {!isOutOfStock && (
              <div
                className="inline-flex relative overflow-hidden rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 px-3.5 py-1.5 shadow-md ring-1 ring-amber-300/50"
                data-testid="detail-get-it-now-badge"
              >
                <span className="pointer-events-none absolute inset-0 -translate-x-full animate-[shine_2.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/45 to-transparent" style={{ animation: 'shine 2.5s ease-in-out infinite' }} />
                <div className="relative flex items-center gap-1.5 text-white">
                  <span className="relative flex items-center justify-center">
                    <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-white opacity-75 animate-ping" />
                    <span className="relative inline-block h-1.5 w-1.5 rounded-full bg-white" />
                  </span>
                  <Zap className="h-4 w-4 fill-white" />
                  <span className="text-xs sm:text-sm font-extrabold uppercase tracking-wider">{instantDeliveryLabel}</span>
                </div>
              </div>
            )}

            <div className="flex items-baseline gap-2 sm:gap-3 flex-wrap" data-testid="product-price-section">
              <span className="text-2xl sm:text-4xl font-bold text-primary" data-testid="product-price">&#8377;{displayPrice}</span>
              {hasMrpDiscount && (
                <>
                  <span className="text-base sm:text-xl text-muted-foreground line-through" data-testid="product-mrp">&#8377;{mrpPrice}</span>
                  <span className="text-xs sm:text-sm font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-testid="product-discount">{discountPct}% OFF</span>
                </>
              )}
            </div>

            <p className="text-muted-foreground" data-testid="product-description">{product.description}</p>

            {/* Colour Sibling Switcher — same item in different colours, listed
                as separate products but linked via merge_group_id. Tapping a
                swatch navigates to the sibling product. */}
            {siblings.length > 0 && (
              <div data-testid="sibling-switcher">
                <label className="block text-sm font-medium mb-2">
                  Other colours available
                </label>
                <div className="flex flex-wrap gap-2">
                  {siblings.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => navigate(`/product/${s.id}`)}
                      title={`${s.color || s.name} — ₹${s.price}`}
                      className="group flex items-center gap-2 border border-stone-200 hover:border-primary rounded-xl pl-1 pr-3 py-1 bg-white hover:shadow-md transition-all"
                      data-testid={`sibling-${s.id}`}
                    >
                      <span className="w-9 h-9 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0">
                        {s.image && <img src={resolveImageUrl(s.image)} alt={s.color || s.name} onError={handleImgError} className="w-full h-full object-cover" />}
                      </span>
                      <span className="text-xs font-medium text-stone-700 group-hover:text-primary">
                        {s.color || s.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection - shown only if more than 1 color available */}
            {availableColors.length > 1 && (
              <div data-testid="color-selector">
                <label className="block text-sm font-medium mb-2">
                  Color {selectedColor && <span className="text-primary ml-1">— {selectedColor}</span>}
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableColors.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => { setSelectedColor(color); setSelectedSize(''); setSelectedImage(0); }}
                      className={`px-4 py-2 border rounded-xl transition-all cursor-pointer text-sm font-medium ${
                        selectedColor === color
                          ? 'border-primary bg-primary text-white shadow-md shadow-primary/20'
                          : 'border-stone-200 hover:border-primary/60 bg-white hover:shadow-sm'
                      }`}
                      data-testid={`color-option-${color}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Show color label when only 1 color (auto-selected, no picker needed) */}
            {availableColors.length === 1 && selectedColor && (
              <p className="text-sm text-muted-foreground" data-testid="single-color-label">
                Color: <span className="font-medium text-foreground">{selectedColor}</span>
              </p>
            )}

            {/* Size Selection — show whenever sizes exist (even without colors) and category supports sizes */}
            {showSizes && sizesForColor.length > 0 && (
              <div data-testid="size-selector">
                <label className="block text-sm font-medium mb-2">
                  Select size <span className="text-red-500">*</span>
                  {selectedSize && <span className="text-primary ml-1">— {selectedSize}</span>}
                  {hasInventory && selectedColor && <span className="text-xs text-muted-foreground ml-2">(Available for {selectedColor})</span>}
                </label>
                <div className="flex flex-wrap gap-2">
                  {sizesForColor.map(size => {
                    const sizeQty = hasInventory && selectedColor ? (parseInt((inventory[selectedColor] || {})[size]) || 0) : null;
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border rounded-xl transition-all cursor-pointer text-sm relative ${
                          selectedSize === size
                            ? 'border-primary bg-primary text-white shadow-md shadow-primary/20'
                            : 'border-stone-200 hover:border-primary/60 bg-white hover:shadow-sm'
                        }`}
                        data-testid={`size-option-${size}`}
                      >
                        {size}
                        {hasFlatVariants && variantPrices[size] != null && (
                          <span className={`block text-[10px] mt-0.5 font-semibold ${selectedSize === size ? 'text-white/90' : 'text-primary'}`}>
                            &#8377;{Number(variantPrices[size])}
                          </span>
                        )}
                        {sizeQty !== null && sizeQty > 0 && sizeQty < LOW_STOCK_THRESHOLD && (
                          <span className={`block text-[10px] mt-0.5 font-semibold ${selectedSize === size ? 'text-white/90' : 'text-orange-600'}`}>
                            Hurry! Few left
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {!selectedSize && (
                  <p className="text-[11px] text-amber-600 mt-1.5">Please pick a size to continue</p>
                )}
              </div>
            )}

            {/* No sizes for this category but colors selected - show stock state without exposing units */}
            {selectedColor && !showSizes && availableColors.length > 0 && selectedStock !== null && (
              <div data-testid="stock-info-color">
                <StockBadge stock={selectedStock} />
              </div>
            )}

            {/* Stock indicator when no color/size selection needed */}
            {!isOutOfStock && availableColors.length === 0 && (product.stock_quantity || 0) > 0 && (
              <div data-testid="stock-info-simple">
                <StockBadge stock={product.stock_quantity} />
              </div>
            )}

            {/* Stock indicator after size selection */}
            {selectedSize && selectedStock !== null && !(!isOutOfStock && availableColors.length === 0 && (product.stock_quantity || 0) > 0) && (
              <div data-testid="stock-info-combo">
                <StockBadge stock={selectedStock} />
              </div>
            )}

            {/* Delivery availability (within Delhi NCR) */}
            <div className="rounded-xl border p-3 flex items-start gap-2.5" data-testid="delivery-info"
              style={{ borderColor: product.delivery_available === false ? '#fde68a' : '#bbf7d0', background: product.delivery_available === false ? '#fffbeb' : '#f0fdf4' }}>
              <Truck className={`h-4 w-4 mt-0.5 flex-shrink-0 ${product.delivery_available === false ? 'text-amber-600' : 'text-green-600'}`} />
              <div className="text-xs leading-snug">
                {product.delivery_available === false ? (
                  <>
                    <span className="font-semibold text-amber-800">Home delivery not available</span>
                    <span className="block text-amber-700/90">Place your order and collect it directly from the shop.</span>
                  </>
                ) : (Number(product.delivery_fee) || 0) > 0 ? (
                  <>
                    <span className="font-semibold text-green-800">Delivery available within Delhi NCR</span>
                    <span className="block text-green-700/90">Delivery charge: &#8377;{Number(product.delivery_fee)} · or pick up free from the shop.</span>
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-green-800">Free delivery within Delhi NCR</span>
                    <span className="block text-green-700/90">Or pick up directly from the shop.</span>
                  </>
                )}
              </div>
            </div>

            {/* Quantity */}
            <div data-testid="quantity-selector">
              <label className="block text-sm font-medium mb-2">Quantity</label>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 border border-stone-200 rounded-xl hover:bg-stone-50 cursor-pointer font-medium"
                  data-testid="quantity-decrease"
                >
                  -
                </button>
                <span className="px-4 py-2 font-medium min-w-[40px] text-center" data-testid="quantity-value">{quantity}</span>
                <button
                  type="button"
                  onClick={() => {
                    const max = selectedStock !== null && selectedStock > 0 ? selectedStock : 99;
                    setQuantity(Math.min(quantity + 1, max));
                  }}
                  className="px-4 py-2 border border-stone-200 rounded-xl hover:bg-stone-50 cursor-pointer font-medium"
                  data-testid="quantity-increase"
                >
                  +
                </button>
              </div>
            </div>

            {/* Purchase actions — Add to Cart / Buy Now, then a live cart
                quantity stepper + Go to Cart once the item is in the cart. */}
            <div className="space-y-3 hidden lg:block" data-testid="purchase-actions">
              {inCartQty === 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleAddToCart}
                    size="lg"
                    disabled={!canAddToCart}
                    className={`w-full rounded-full px-4 py-6 font-medium shadow-lg ${
                      canAddToCart
                        ? 'bg-white border-2 border-primary text-primary hover:bg-primary/5'
                        : 'bg-stone-200 text-stone-500 cursor-not-allowed border-2 border-transparent'
                    }`}
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    {isOutOfStock ? 'Out of Stock' : !selectedColor && availableColors.length > 0 ? 'Select Color' : showSizes && !selectedSize && selectedColor ? 'Select Size' : 'Add to Cart'}
                  </Button>
                  <Button
                    onClick={handleBuyNow}
                    size="lg"
                    disabled={!canAddToCart}
                    className={`w-full rounded-full px-4 py-6 font-bold shadow-lg ${
                      canAddToCart
                        ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-white'
                        : 'bg-stone-200 text-stone-500 cursor-not-allowed'
                    }`}
                    data-testid="buy-now-button"
                  >
                    <Zap className="mr-2 h-5 w-5 fill-white" />
                    Buy Now
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center border-2 border-primary rounded-full overflow-hidden flex-shrink-0" data-testid="cart-qty-stepper">
                      <button
                        type="button"
                        onClick={decrementCartQty}
                        className="px-4 py-3 text-primary hover:bg-primary/5 font-bold text-lg transition-colors"
                        data-testid="cart-qty-decrease"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="px-3 font-semibold min-w-[36px] text-center text-primary" data-testid="cart-qty-value">{inCartQty}</span>
                      <button
                        type="button"
                        onClick={incrementCartQty}
                        className="px-4 py-3 text-primary hover:bg-primary/5 font-bold text-lg transition-colors"
                        data-testid="cart-qty-increase"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    <Button
                      onClick={() => navigate('/cart')}
                      size="lg"
                      className="flex-1 rounded-full px-6 py-6 font-medium shadow-lg bg-primary hover:bg-purple-900 text-white"
                      data-testid="go-to-cart-button"
                    >
                      <ShoppingCart className="mr-2 h-5 w-5" />
                      Go to Cart
                    </Button>
                  </div>
                  <Button
                    onClick={handleBuyNow}
                    size="lg"
                    className="w-full rounded-full px-8 py-6 font-bold shadow-lg bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-white"
                    data-testid="buy-now-button"
                  >
                    <Zap className="mr-2 h-5 w-5 fill-white" />
                    Buy Now
                  </Button>
                  <p className="text-xs text-green-600 flex items-center gap-1" data-testid="in-cart-indicator">
                    <Check className="h-3.5 w-3.5" /> {inCartQty} in your cart{(selectedColor || selectedSize) ? ` (${[selectedColor, selectedSize].filter(Boolean).join(', ')})` : ''}
                  </p>
                </>
              )}
            </div>

            {/* Product Specs */}
            <div className="border-t pt-6 space-y-3" data-testid="product-specs">
              <h3 className="font-semibold text-lg">Product Details</h3>
              {product.fabric && <p className="text-sm"><span className="font-medium">Fabric:</span> {product.fabric}</p>}
              {product.care_instructions && <p className="text-sm"><span className="font-medium">Care:</span> {product.care_instructions}</p>}
              {product.sole_material && <p className="text-sm"><span className="font-medium">Sole Material:</span> {product.sole_material}</p>}
              {product.heel_height_cm > 0 && <p className="text-sm"><span className="font-medium">Heel Height:</span> {product.heel_height_cm} cm</p>}
              {product.dimensions && (() => {
                const d = product.dimensions || {};
                const u = (d.unit || 'cm').toLowerCase();
                const parts = [];
                if (d.length) parts.push(`${d.length} ${u} L`);
                if (d.width) parts.push(`${d.width} ${u} W`);
                if (d.height) parts.push(`${d.height} ${u} H`);
                if (!parts.length) return null;
                return (
                  <p className="text-sm" data-testid="product-dimensions">
                    <span className="font-medium">Dimensions:</span> {parts.join(' × ')}
                  </p>
                );
              })()}
              {(() => {
                const cat = (product.category || '').toLowerCase();
                const isMenOnly = /\b(men|man|boys?|male)\b/.test(cat);
                const isWomenOnly = /\b(women|woman|girls?|female|ladies)\b/.test(cat);
                const isGenderedCat = isMenOnly || isWomenOnly;
                // Respect explicit vendor-set gender when category is gender-neutral
                const hasExplicitGender = product.gender && !['unisex', '', null].includes(String(product.gender).toLowerCase());
                if (isMenOnly) return <p className="text-sm"><span className="font-medium">Gender:</span> Men</p>;
                if (isWomenOnly) return <p className="text-sm"><span className="font-medium">Gender:</span> Women</p>;
                if (!isGenderedCat && hasExplicitGender) {
                  return <p className="text-sm"><span className="font-medium">Gender:</span> {product.gender}</p>;
                }
                return null;
              })()}
            </div>

            {/* Vendor Info */}
            {vendor && (
              <div className="border-t pt-6" data-testid="vendor-info">
                <h3 className="font-semibold text-lg mb-3">Vendor Information</h3>
                <div className="space-y-2">
                  <p className="font-medium">{vendor.shop_name}</p>
                  {vendor.fulfillment_mode === 'delivery' ? (
                    <div className="inline-flex items-center gap-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1" data-testid="vendor-delivery-only">
                      <Truck className="h-4 w-4" /> Delivery only — no in-store pickup
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span>{vendor.market_location}</span>
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground">{vendor.business_hours}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Returns & Replacement disclaimer — uniform across every product page.
            MyBuzzar is a marketplace, not the seller. Vendors set their own
            return/replacement policy and the platform stays neutral. The
            disclaimer is intentionally short and high-contrast so customers
            actually read it before placing the order. */}
        <div
          className="border-t pt-8 mt-8"
          data-testid="returns-replacement-disclaimer"
        >
          {(() => {
            const vendorPolicy = (vendor?.return_policy || '').trim();
            if (vendorPolicy) {
              return (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5 flex gap-3" data-testid="vendor-return-policy">
                  <div className="shrink-0 mt-0.5">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 text-base font-bold">↺</span>
                  </div>
                  <div className="text-sm leading-relaxed min-w-0">
                    <h3 className="font-semibold text-emerald-900 mb-1">
                      Returns &amp; Replacement
                      {vendor?.shop_name ? <span className="font-normal text-emerald-800/70"> · {vendor.shop_name}</span> : null}
                    </h3>
                    <p className="text-emerald-900/90 whitespace-pre-line">{vendorPolicy}</p>
                    <p className="mt-2 text-xs text-emerald-800/70">
                      Policy set by the seller. For more help,{' '}
                      <Link to="/support" className="font-semibold underline underline-offset-2 hover:text-emerald-700" data-testid="returns-disclaimer-support-link">
                        contact MyBuzzar Support →
                      </Link>
                    </p>
                  </div>
                </div>
              );
            }
            return (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5 flex gap-3">
                <div className="shrink-0 mt-0.5">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-700 text-base font-bold">!</span>
                </div>
                <div className="text-sm leading-relaxed">
                  <h3 className="font-semibold text-amber-900 mb-1">Returns &amp; Replacement</h3>
                  <p className="text-amber-900/90">
                    Directly contact the vendor for return and replacement. For more help,{' '}
                    <Link
                      to="/support"
                      className="font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                      data-testid="returns-disclaimer-support-link"
                    >
                      contact MyBuzzar Support →
                    </Link>
                  </p>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Reviews Section */}
        <div className="border-t pt-12" data-testid="reviews-section">
          <h2 className="text-2xl font-playfair font-bold mb-6">Customer Reviews</h2>

          {user && canReview && (
            <form onSubmit={handleSubmitReview} className="mb-8 p-6 bg-white rounded-lg border" data-testid="review-form">
              <h3 className="font-semibold mb-4">Write a Review</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Rating</label>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setReviewRating(rating)}
                        data-testid={`rating-star-${rating}`}
                      >
                        <Star
                          className={`h-6 w-6 ${
                            rating <= reviewRating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-stone-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Your Review</label>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    className="w-full p-3 border border-stone-200 rounded-lg"
                    rows={4}
                    required
                    data-testid="review-text-input"
                  />
                </div>
                <Button type="submit" disabled={submittingReview} data-testid="submit-review-button">
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </Button>
              </div>
            </form>
          )}

          {user && !canReview && (
            <p className="mb-6 text-sm text-muted-foreground bg-stone-50 p-4 rounded-lg border border-stone-200" data-testid="review-restricted-msg">
              <Check className="h-4 w-4 inline mr-1.5 text-stone-400" />
              Only verified buyers can write reviews. Complete a purchase of this product to leave your review.
            </p>
          )}

          <div className="space-y-4">
            {reviews.length > 0 ? (
              reviews.map(review => (
                <div key={review.id} className="p-6 bg-white rounded-lg border" data-testid={`review-${review.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{review.user_name}</p>
                        {review.verified_purchase && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-testid="verified-badge">Verified Purchase</span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={`review-star-${i}`}
                              className={`h-4 w-4 ${
                                i < review.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-stone-300'
                              }`}
                            />
                          ))}
                        </div>
                        {review.verified_purchase && (
                          <span className="text-xs text-secondary flex items-center">
                            <Check className="h-3 w-3 mr-1" /> Verified Purchase
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{review.comment}</p>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8" data-testid="no-reviews">No reviews yet. Be the first to review!</p>
            )}
          </div>
        </div>

        {/* More from this Vendor Section */}
        {vendorProfile && moreFromVendor.length > 0 && (
          <div className="mt-12 border-t pt-8" data-testid="more-from-vendor">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Store className="h-6 w-6 text-primary" />
                <div>
                  <h2 className="text-xl font-semibold">More from {vendorProfile.shop_name}</h2>
                  {vendorProfile.fulfillment_mode !== 'delivery' && (
                    <p className="text-sm text-muted-foreground">{vendorProfile.market_location}</p>
                  )}
                </div>
              </div>
              <Link 
                to={`/vendor/${vendorProfile.id}/shop`}
                className="text-primary hover:underline flex items-center gap-1 text-sm font-medium"
                data-testid="view-all-vendor-products"
              >
                View All Products <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {moreFromVendor.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile purchase bar — keeps Add to Cart / Buy Now within reach
          without scrolling. Hidden on lg+ where the inline actions are visible. */}
      {!isPreviewMode && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-stone-200 px-3 py-2.5 shadow-[0_-6px_24px_rgba(0,0,0,0.08)]" data-testid="sticky-purchase-bar">
          {inCartQty === 0 ? (
            <div className="grid grid-cols-2 gap-2.5">
              <Button
                onClick={handleAddToCart}
                disabled={!canAddToCart}
                className={`w-full rounded-full py-5 text-sm font-semibold ${canAddToCart ? 'bg-white border-2 border-primary text-primary hover:bg-primary/5' : 'bg-stone-200 text-stone-500 cursor-not-allowed border-2 border-transparent'}`}
                data-testid="sticky-add-to-cart-button"
              >
                <ShoppingCart className="mr-1.5 h-4 w-4" />
                {isOutOfStock ? 'Out of Stock' : !selectedColor && availableColors.length > 0 ? 'Select Color' : showSizes && !selectedSize && selectedColor ? 'Select Size' : 'Add to Cart'}
              </Button>
              <Button
                onClick={handleBuyNow}
                disabled={!canAddToCart}
                className={`w-full rounded-full py-5 text-sm font-bold ${canAddToCart ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-white' : 'bg-stone-200 text-stone-500 cursor-not-allowed'}`}
                data-testid="sticky-buy-now-button"
              >
                <Zap className="mr-1.5 h-4 w-4 fill-white" /> Buy Now
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <div className="flex items-center border-2 border-primary rounded-full overflow-hidden flex-shrink-0">
                <button type="button" onClick={decrementCartQty} className="px-3.5 py-2.5 text-primary font-bold text-lg" aria-label="Decrease quantity" data-testid="sticky-cart-qty-decrease">−</button>
                <span className="px-2 font-semibold min-w-[28px] text-center text-primary" data-testid="sticky-cart-qty-value">{inCartQty}</span>
                <button type="button" onClick={incrementCartQty} className="px-3.5 py-2.5 text-primary font-bold text-lg" aria-label="Increase quantity" data-testid="sticky-cart-qty-increase">+</button>
              </div>
              <Button onClick={handleBuyNow} className="flex-1 rounded-full py-5 text-sm font-bold bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-white" data-testid="sticky-buy-now-button">
                <Zap className="mr-1.5 h-4 w-4 fill-white" /> Buy Now
              </Button>
              <Button onClick={() => navigate('/cart')} className="rounded-full py-5 px-4 text-sm font-semibold bg-primary hover:bg-purple-900 text-white" data-testid="sticky-go-to-cart-button">
                <ShoppingCart className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Image Lightbox with Zoom & Pan */}
      {lightboxOpen && displayImages.length > 0 && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" data-testid="image-lightbox"
          onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}>
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0 z-10">
            <div className="flex items-center gap-2">
              <span className="text-white/50 text-xs">{selectedImage + 1} / {displayImages.length}</span>
              {zoom > 1 && <span className="text-white/40 text-xs bg-white/10 px-2 py-0.5 rounded-full">{Math.round(zoom * 100)}%</span>}
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setZoom(z => Math.min(5, z + 0.5))} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition" title="Zoom in">
                <ZoomIn className="h-4 w-4" />
              </button>
              <button onClick={() => { setZoom(z => { const n = Math.max(1, z - 0.5); if (n === 1) setPan({x:0,y:0}); return n; }); }} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition" title="Zoom out">
                <ZoomOut className="h-4 w-4" />
              </button>
              <button onClick={resetZoom} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition" title="Reset">
                <RotateCcw className="h-4 w-4" />
              </button>
              <button onClick={() => { setLightboxOpen(false); resetZoom(); }} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition ml-2" title="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Image area */}
          <div className="flex-1 relative overflow-hidden flex items-center justify-center"
            onWheel={handleWheel}>
            {displayImages.length > 1 && (
              <>
                <button onClick={() => { setSelectedImage(i => (i - 1 + displayImages.length) % displayImages.length); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 z-10 transition">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => { setSelectedImage(i => (i + 1) % displayImages.length); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 z-10 transition">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
            <div className="w-full h-full flex items-center justify-center"
              style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onDoubleClick={handleDoubleClick}>
              <img
                src={resolveImageUrl(displayImages[selectedImage])}
                alt={product?.name}
                draggable={false}
                className="max-w-full max-h-full object-contain select-none transition-transform duration-150"
                style={{ transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)` }}
                onError={handleImgError}
              />
            </div>
            {zoom === 1 && (
              <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/30 text-xs bg-black/40 px-3 py-1 rounded-full">
                Scroll to zoom &middot; Double-click to enlarge
              </p>
            )}
          </div>

          {/* Thumbnail strip */}
          {displayImages.length > 1 && (
            <div className="flex items-center justify-center gap-2 py-3 px-4 flex-shrink-0">
              {displayImages.map((img, i) => (
                <button key={i} onClick={() => setSelectedImage(i)}
                  className={`w-11 h-11 rounded-lg overflow-hidden border-2 transition flex-shrink-0 ${i === selectedImage ? 'border-white scale-110' : 'border-white/20 opacity-50 hover:opacity-80'}`}>
                  <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" draggable={false} onError={handleImgError} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
